import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { createQuoteChangeOrderComment } from '../../../gql/ChangeOrder/mutations/CreateQuoteChangeOrderComment';
import { listQuoteChangeOrderComments } from '../../../gql/ChangeOrder/queries/ListQuoteChangeOrderComments';
import { ChangeOrderComment, CommentListResponse, CommentVisibility } from '../../../interfaces/ChangeOrderComment';
import { AppDispatch, RootState } from '../../../store';
import { UIInput } from '../../../types/uiTypes';
import { convertSystemErrorToString } from '../../../util/errorUtils';

export const SLICE_NAME = 'changeOrderComments';

export interface ChangeOrderCommentsState {
  comments: ChangeOrderComment[];
  nextToken?: string;
  status: {
    isLoadingComments: boolean;
    isCreatingComment: boolean;
  };
  errors: {
    loadingCommentsError: string;
    creatingCommentError: string;
  };
}

interface SetStatusParam {
  statusKey: keyof ChangeOrderCommentsState['status'];
  status: ChangeOrderCommentsState['status'][keyof ChangeOrderCommentsState['status']];
}

interface SetErrorParam {
  errorKey: keyof ChangeOrderCommentsState['errors'];
  error: ChangeOrderCommentsState['errors'][keyof ChangeOrderCommentsState['errors']];
}

const initialState: ChangeOrderCommentsState = {
  comments: [],
  nextToken: undefined,
  status: {
    isLoadingComments: false,
    isCreatingComment: false
  },
  errors: {
    loadingCommentsError: '',
    creatingCommentError: ''
  }
};

const changeOrderCommentsSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setComments(state, action: PayloadAction<CommentListResponse>) {
      state.comments = action.payload.comments;
      state.nextToken = action.payload.nextToken;
    },
    addComment(state, action: PayloadAction<ChangeOrderComment>) {
      state.comments.unshift(action.payload); // Add to beginning for newest first
    },
    setCommentsStatus(state, action: PayloadAction<SetStatusParam>) {
      const { statusKey, status } = action.payload;
      state.status[statusKey] = status;
    },
    setCommentsError(state, action: PayloadAction<SetErrorParam>) {
      const { errorKey, error } = action.payload;
      state.errors[errorKey] = error;
    },
    clearComments(state) {
      state.comments = [];
      state.nextToken = undefined;
    }
  }
});

export interface LoadCommentsUIInput extends UIInput {
  changeOrderId: string;
  maxResults?: number;
  nextToken?: string;
}

export interface CreateCommentUIInput extends UIInput {
  changeOrderId: string;
  message: string;
  visibility: CommentVisibility;
}

/**
 * Load comments for a change order
 */
export const onLoadComments = createAsyncThunk<
  void,
  LoadCommentsUIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(
  `${SLICE_NAME}/onLoadComments`,
  async ({ apolloClient, changeOrderId, maxResults, nextToken }, { dispatch }): Promise<void> => {
    try {
      dispatch(setCommentsStatus({ statusKey: 'isLoadingComments', status: true }));
      dispatch(setCommentsError({ errorKey: 'loadingCommentsError', error: '' }));
      
      const response = await listQuoteChangeOrderComments(apolloClient, {
        changeOrderId,
        maxResults,
        nextToken
      });
      
      dispatch(setComments(response));
    } catch (error) {
      const sanitizedError = convertSystemErrorToString(error);
      dispatch(setCommentsError({ errorKey: 'loadingCommentsError', error: sanitizedError }));
    } finally {
      dispatch(setCommentsStatus({ statusKey: 'isLoadingComments', status: false }));
    }
  }
);

/**
 * Create a new comment for a change order
 */
export const onCreateComment = createAsyncThunk<
  void,
  CreateCommentUIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(
  `${SLICE_NAME}/onCreateComment`,
  async ({ apolloClient, changeOrderId, message, visibility }, { dispatch }): Promise<void> => {
    try {
      dispatch(setCommentsStatus({ statusKey: 'isCreatingComment', status: true }));
      dispatch(setCommentsError({ errorKey: 'creatingCommentError', error: '' }));
      
      await createQuoteChangeOrderComment(apolloClient, {
        changeOrderId,
        message,
        visibility
      });
      
      // Reload comments after successful creation
      dispatch(onLoadComments({ apolloClient, changeOrderId }));
    } catch (error) {
      const sanitizedError = convertSystemErrorToString(error);
      dispatch(setCommentsError({ errorKey: 'creatingCommentError', error: sanitizedError }));
    } finally {
      dispatch(setCommentsStatus({ statusKey: 'isCreatingComment', status: false }));
    }
  }
);

export const {
  setComments,
  addComment,
  setCommentsStatus,
  setCommentsError,
  clearComments
} = changeOrderCommentsSlice.actions;

export const selectChangeOrderCommentsState = (state: RootState) => state[SLICE_NAME];

// Selector to get comments filtered by visibility
export const selectCommentsByVisibility = (visibility: CommentVisibility) => (state: RootState) => {
  return state[SLICE_NAME].comments.filter(comment => comment.visibility === visibility);
};

export default changeOrderCommentsSlice.reducer;
